import { SDK_ERRORS, type SdkCapability } from '@riz/contracts/sdk';
import { rejectWith } from './errors.ts';

/**
 * The capability set arrives at handshake from the control plane's per-game
 * matrix. An absent capability is a granted-elsewhere feature this game may not
 * use, never a missing method: the module still exists and rejects, so a game
 * written against a capability it later loses fails in its error branch rather
 * than on a `TypeError`.
 */
export class CapabilityRegistry {
  private granted = new Set<string>();
  private resolved = false;

  set(capabilities: readonly string[] | null | undefined): void {
    this.granted = new Set(capabilities ?? []);
    this.resolved = true;
  }

  clear(): void {
    this.granted.clear();
    this.resolved = false;
  }

  /** False before the handshake resolves — a game must await `init()` first. */
  has(capability: SdkCapability | string): boolean {
    return this.resolved && this.granted.has(capability);
  }

  list(): SdkCapability[] {
    return [...this.granted] as SdkCapability[];
  }

  require<T>(capability: SdkCapability, moduleLabel: string): Promise<T> | null {
    if (this.has(capability)) return null;
    return rejectWith(
      SDK_ERRORS.capabilityDisabled,
      `${moduleLabel} is not enabled for this game. Ask Riz to enable "${capability}".`,
      { capability },
    );
  }
}
